import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import { Button, Modal, FormField, EmptyState } from '../../components/ui';
import DataTable from '../../components/DataTable';

// Inline ikonlar (proje genel pattern'i — Lucide tarzı stroke SVG)
const IconExcel  = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="21"/><line x1="16" y1="13" x2="8" y2="21"/></svg>);
const IconPlus   = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>);
const IconEdit   = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>);
const IconTrash    = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>);
const IconUpload   = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>);

export default function PergolaTemplatesPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/pergola/templates');
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew  = () => { setError(''); setEditing({ product_name: '', file: null, existing_filename: null }); };
  const openEdit = (row) => { setError(''); setEditing({ id: row.id, product_name: row.product_name, file: null, existing_filename: row.excel_filename }); };
  const cancelEdit = () => { setEditing(null); setError(''); };

  const save = async () => {
    if (!editing.product_name.trim()) { setError('Ürün adı zorunludur'); return; }
    setSaving(true); setError('');
    try {
      const fd = new FormData();
      fd.append('product_name', editing.product_name.trim());
      if (editing.file) fd.append('excel_file', editing.file);
      const opts = { headers: { 'Content-Type': 'multipart/form-data' } };
      if (editing.id) await api.put('/pergola/templates/' + editing.id, fd, opts);
      else            await api.post('/pergola/templates', fd, opts);
      setEditing(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`"${row.product_name}" silinsin mi?`)) return;
    try {
      await api.delete('/pergola/templates/' + row.id);
      await load();
    } catch (e) {
      alert(e.response?.data?.error || 'Silme başarısız');
    }
  };

  const fmtSize = (n) => {
    if (!n) return '—';
    if (n < 1024) return n + ' B';
    if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
    return (n / (1024 * 1024)).toFixed(2) + ' MB';
  };
  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('tr-TR') : '';

  return (
    <div className="stocks-page">
      <div className="stocks-topbar">
        <div className="stocks-topbar-left">
          <h2>Persan Tablo Şablonları</h2>
          <span className="stocks-count-badge">{list.length} şablon</span>
        </div>
        <div className="stocks-topbar-right">
          <Button size="sm" icon={<IconPlus />} onClick={openNew}>Yeni Şablon</Button>
        </div>
      </div>

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="pergola-templates-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText=""
          data={list}
          rowKey="id"
          onRowClick={openEdit}
          columns={[
            { id: 'product_name', header: 'Ürün Adı', accessor: 'product_name', sortable: true, width: 280,
              cell: r => <strong className="stock-name-cell">{r.product_name}</strong> },
            { id: 'excel_filename', header: 'Excel Dosyası', accessor: 'excel_filename', sortable: true, width: 320,
              cell: r => r.excel_filename ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--accent-primary, #16a34a)' }}><IconExcel /></span>
                  {r.excel_filename}
                </span>
              ) : (
                <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>— dosya yok</span>
              ) },
            { id: 'file_size', header: 'Boyut',
              accessor: r => parseInt(r.file_size) || 0, sortable: true, width: 100,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: r => <span className="stock-secondary">{fmtSize(r.file_size)}</span> },
            { id: 'created_at', header: 'Eklendi', accessor: 'created_at', sortable: true, width: 130,
              cell: r => <span className="stock-secondary">{fmtDate(r.created_at)}</span> },
            { id: 'actions', header: '', sortable: false, hideable: false, draggable: false, filterable: false, resizable: false, width: 130,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', whiteSpace: 'nowrap' },
              cell: r => (
                <span onClick={e => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" iconOnly icon={<IconEdit />} title="Düzenle" onClick={() => openEdit(r)} />
                  {' '}
                  <Button variant="ghost" size="sm" iconOnly icon={<IconTrash />} title="Sil" onClick={() => remove(r)} />
                </span>
              ) },
          ]}
        />
        {!loading && list.length === 0 && (
          <div style={{ padding: 24 }}>
            <EmptyState
              title="Henüz şablon yok"
              description="Pergole modeli için ürün adı ve hesap formülü içeren Excel dosyası ekleyin."
              compact
            />
          </div>
        )}
      </div>

      {editing && (
        <Modal
          icon={<IconExcel />}
          title={editing.id ? 'Şablonu Düzenle' : 'Yeni Persan Tablo Şablonu'}
          subtitle="Ürün adı + hesap formülü içeren Excel dosyası"
          size="md"
          onClose={cancelEdit}
          primaryAction={{ label: 'Kaydet', onClick: save, loading: saving }}
          cancelAction={{ label: 'Vazgeç', onClick: cancelEdit }}
        >
          <FormField label="Ürün Adı" required error={error && !editing.product_name.trim() ? error : null}>
            <input
              type="text"
              value={editing.product_name}
              onChange={e => setEditing({ ...editing, product_name: e.target.value })}
              placeholder="Örn: Bioclimatic Pergola 4×5"
              autoFocus
            />
          </FormField>

          <FormField
            label="Excel Dosyası"
            hint=".xlsx, .xls, .xlsm, .csv — max 10 MB"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <label className="ui-btn ui-btn-ghost ui-btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
                <span className="ui-btn-ico"><IconUpload /></span>
                <span>Dosya Seç</span>
                <input
                  type="file"
                  accept=".xlsx,.xls,.xlsm,.csv"
                  onChange={e => setEditing({ ...editing, file: e.target.files[0] || null })}
                  style={{ display: 'none' }}
                />
              </label>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {editing.file ? (
                  <><strong>{editing.file.name}</strong> ({fmtSize(editing.file.size)})</>
                ) : editing.existing_filename ? (
                  <>Mevcut: <strong>{editing.existing_filename}</strong> <span style={{ color: 'var(--text-tertiary)' }}>(yenisi seçilmezse korunur)</span></>
                ) : (
                  <span style={{ color: 'var(--text-tertiary)' }}>Henüz dosya seçilmedi</span>
                )}
              </span>
            </div>
          </FormField>

          {error && editing.product_name.trim() && (
            <div className="ui-field-error" style={{ marginTop: 8 }}>{error}</div>
          )}
        </Modal>
      )}
    </div>
  );
}
